<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue';
import QRCode from 'qrcode';
import { useTopupStore } from '../stores/topup';
import { useAuthStore } from '../stores/auth';
import { useKeeperProvider } from '../lib/keeper-provider';
import TopupPayModal from '../components/TopupPayModal.vue';
import { formatNgonka, ngonkaToGnk, formatDateTime, shortId } from '../lib/format';

const topup = useTopupStore();
const auth = useAuthStore();
const { provider: keeperProvider, status: keeperStatus } = useKeeperProvider();

const showPayModal = ref(false);
const toastMessage = ref<string | null>(null);
let toastTimer: number | null = null;

const chainId = computed(() => auth.config?.chainId ?? '');
const bech32Prefix = computed(() => auth.config?.bech32Prefix ?? 'gonka');

function openPayModal(): void {
  if (keeperStatus.value !== 'found' || !keeperProvider.value) return;
  showPayModal.value = true;
}

function showToast(msg: string): void {
  toastMessage.value = msg;
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastMessage.value = null;
  }, 6000);
}

function openInstallPage(): void {
  window.open('https://gonkakeeper.com', '_blank', 'noopener,noreferrer');
}

function onPaySuccess(txHash: string, amountNgonka: string): void {
  showPayModal.value = false;
  topup.setPendingTx({ txHash, expectedAmountNgonka: amountNgonka, broadcastedAt: Date.now() });
  const gnk = (Number(BigInt(amountNgonka)) / 1_000_000_000).toString();
  showToast(`Sent ${gnk} GNK, waiting for confirmation`);
  // Coarse poll: watcher needs a block (~6s) before the tx shows up in /recent.
  window.setTimeout(() => {
    void topup.refresh();
  }, 6000);
}

const qr = ref<string | null>(null);
const copied = ref(false);
const memoCopied = ref(false);

watchEffect(async () => {
  const uri = topup.depositInfo?.paymentUri;
  if (!uri) {
    qr.value = null;
    return;
  }
  try {
    qr.value = await QRCode.toDataURL(uri, {
      margin: 1,
      width: 240,
      color: { dark: '#e6edf3', light: '#0d1117' },
    });
  } catch {
    qr.value = null;
  }
});

async function copyAddress(): Promise<void> {
  const addr = topup.depositInfo?.address;
  if (!addr) return;
  try {
    await navigator.clipboard.writeText(addr);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    // Ignore — clipboard API can fail in restricted contexts; user can copy manually.
  }
}

async function copyMemo(): Promise<void> {
  const memo = topup.depositInfo?.memo;
  if (!memo) return;
  try {
    await navigator.clipboard.writeText(memo);
    memoCopied.value = true;
    setTimeout(() => {
      memoCopied.value = false;
    }, 1500);
  } catch {
    // Ignore — clipboard API can fail in restricted contexts; user can copy manually.
  }
}

onMounted(async () => {
  await topup.loadAll();
});
</script>

<template>
  <div class="max-w-4xl mx-auto px-6 py-8 flex flex-col gap-6">
    <!-- Header: back arrow + title (matches reference img_3.png) -->
    <header class="flex items-center gap-3">
      <router-link
        to="/billing"
        class="text-muted hover:text-ink transition"
        aria-label="Back to Billing"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </router-link>
      <h1 class="text-xl font-semibold">Top Up Balance</h1>
    </header>

    <!-- One-click pay via GonkaKeeper (T12) -->
    <section class="card p-4 flex flex-col gap-3">
      <div class="flex items-center justify-between gap-3">
        <div>
          <div class="text-sm font-medium">Pay with GonkaKeeper</div>
          <div class="text-xs text-muted">
            One click — sign in the extension and we'll handle the rest.
          </div>
        </div>
        <div class="shrink-0">
          <button
            v-if="keeperStatus === 'found'"
            class="btn-primary text-sm"
            type="button"
            @click="openPayModal"
          >
            ⚡ Pay with GonkaKeeper
          </button>
          <button
            v-else-if="keeperStatus === 'missing'"
            class="btn-ghost text-sm"
            type="button"
            @click="openInstallPage"
          >
            Install GonkaKeeper to pay in one click
          </button>
          <button v-else class="btn-ghost text-sm" type="button" disabled>
            Detecting wallet…
          </button>
        </div>
      </div>
    </section>

    <!-- Memo requirement warning (T11) -->
    <section class="card p-4 flex gap-3 items-start" style="border-color:#f0883e;">
      <div class="text-warn text-lg leading-none">⚠</div>
      <div class="text-sm">
        <div class="font-medium text-warn mb-1">Memo is required for non-GonkaKeeper deposits</div>
        <div class="text-muted">
          When sending from an exchange or any wallet other than GonkaKeeper, you
          <strong class="text-ink">must</strong> include the memo below. Without the
          memo your deposit will not be credited to your account. GonkaKeeper users can scan
          the QR — the memo is included automatically.
        </div>
      </div>
    </section>

    <!-- Top up your balance -->
    <section class="card p-6">
      <h2 class="text-sm uppercase tracking-wide text-muted mb-4">Top up your balance</h2>

      <div v-if="topup.error" class="text-danger text-sm mb-4">{{ topup.error }}</div>

      <div v-if="!topup.depositInfo && topup.loading" class="text-muted text-sm">Loading…</div>

      <div v-else-if="topup.depositInfo" class="grid sm:grid-cols-2 gap-6 items-start">
        <!-- QR -->
        <div class="flex justify-center sm:justify-start">
          <img
            v-if="qr"
            :src="qr"
            alt="Gonka top-up payment QR code"
            width="240"
            height="240"
            class="rounded border border-border bg-canvas"
          />
          <div v-else class="w-[240px] h-[240px] rounded border border-border bg-code" />
        </div>

        <!-- Address + memo + meta -->
        <div class="flex flex-col gap-3">
          <div class="text-xs text-muted uppercase tracking-wide">Send GNK to this address</div>
          <div class="font-mono break-all text-sm bg-code rounded px-3 py-2 border border-border">
            {{ topup.depositInfo.address }}
          </div>
          <div>
            <button class="btn-ghost text-xs" type="button" @click="copyAddress">
              {{ copied ? 'Copied!' : 'Copy address' }}
            </button>
          </div>

          <div>
            <div class="text-xs uppercase tracking-wide text-warn mt-2">Memo (REQUIRED)</div>
            <div class="mt-2 font-mono break-all text-sm bg-code rounded px-3 py-2 border border-border">
              {{ topup.depositInfo.memo }}
            </div>
            <button class="btn-ghost text-xs mt-2" type="button" @click="copyMemo">
              {{ memoCopied ? 'Copied!' : 'Copy memo' }}
            </button>
          </div>

          <div class="text-xs text-muted uppercase tracking-wide mt-2">Minimum amount</div>
          <div class="flex items-baseline gap-3 flex-wrap">
            <div class="text-sm">
              {{ formatNgonka(topup.depositInfo.minNgonka) }}
              <span class="text-muted ml-1">ngonka</span>
            </div>
            <div class="text-sm text-muted">
              {{ ngonkaToGnk(topup.depositInfo.minNgonka) }} GNK
            </div>
          </div>

          <div class="text-xs text-muted mt-3">
            On-chain deposits usually appear in your balance within ~30 seconds after the
            transaction is confirmed.
          </div>
          <div>
            <button class="btn-ghost text-xs" type="button" @click="topup.refresh()">
              Refresh
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Recent top-ups -->
    <section class="card p-6">
      <h2 class="text-sm uppercase tracking-wide text-muted mb-4">Recent top-ups</h2>

      <div v-if="topup.error" class="text-danger text-sm mb-4">{{ topup.error }}</div>

      <div v-if="topup.loading && topup.recent.length === 0" class="text-muted text-sm">Loading…</div>
      <div
        v-else-if="topup.recent.length === 0"
        class="text-muted text-sm italic py-2"
      >
        No top-ups yet. Send GNK to the address above to fund your account.
      </div>
      <table v-else class="w-full table">
        <thead>
          <tr>
            <th>When</th>
            <th>Amount</th>
            <th>Transaction</th>
            <th>Balance after</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in topup.recent" :key="t.id">
            <td class="text-muted">{{ formatDateTime(t.createdAt) }}</td>
            <td class="text-ok">
              {{ formatNgonka(t.amountNgonka) }}
              <span class="text-muted ml-1">ngonka</span>
            </td>
            <td>
              <code v-if="t.txHash">{{ shortId(t.txHash, 6, 6) }}</code>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ formatNgonka(t.balanceAfter) }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- One-click pay modal -->
    <TopupPayModal
      v-if="showPayModal && topup.depositInfo && keeperProvider && chainId"
      :deposit-info="topup.depositInfo"
      :provider="keeperProvider"
      :chain-id="chainId"
      :bech32-prefix="bech32Prefix"
      @close="showPayModal = false"
      @success="onPaySuccess"
    />

    <!-- Toast -->
    <div
      v-if="toastMessage"
      class="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 card px-4 py-3 text-sm shadow-lg"
    >
      {{ toastMessage }}
    </div>
  </div>
</template>
